import { useCallback, useEffect, useMemo, useState } from 'react';
import { Base64 } from 'js-base64';
import { TimesheetLabelValue } from 'graphql/types';

import { useActivityLabelSearch } from 'modules/timesheet-labels/hooks/useActivityLabelSearch';

type UseActivityChipData = {
  options: TimesheetLabelValue[];
  handleSearch: (query: string) => void;
  loading: boolean;
  hasMore: boolean;
};

export const isThereMore = (
  cursor: number | undefined,
  totalCount: number | undefined
): boolean => {
  return cursor && totalCount ? cursor < totalCount : false;
};

export const useActivity = (): UseActivityChipData => {
  const [options, setOptions] = useState<TimesheetLabelValue[]>([]);
  const [lastSearch, setLastSearch] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState<boolean>(false);

  const { searchHandler, data } = useActivityLabelSearch();

  const loading = useMemo(() => Boolean(!data), [data]);

  const handleSearch = useCallback(
    (queryText: string) => {
      if (lastSearch !== queryText) {
        searchHandler({
          query: queryText || undefined
        });
      }
      setLastSearch(queryText);
    },
    [searchHandler, lastSearch, setLastSearch]
  );

  useEffect(() => {
    if (loading) {
      setOptions([]);
      setHasMore(false);
    } else if (data) {
      const activities = data?.timesheetLabelValueSearch?.activities?.items;
      const cursor = data?.timesheetLabelValueSearch?.activities?.cursor
        ? parseInt(
            Base64.decode(data?.timesheetLabelValueSearch?.activities?.cursor)
          )
        : undefined;
      const totalCount =
        data?.timesheetLabelValueSearch?.activities?.totalCount;
      setOptions(activities || []);
      setHasMore(isThereMore(cursor, totalCount));
    }
  }, [data, loading]);

  return useMemo(
    () => ({
      options,
      handleSearch,
      loading,
      hasMore
    }),
    [options, handleSearch, loading, hasMore]
  );
};

export default useActivity;
